import { Suspense, useMemo, type JSX } from 'react';
import { WidgetViewType, type Widget, type WidgetType } from '@/types/widgets';
import { widgetRegistry } from './WidgetRegistry';
import styles from './WidgetRenderer.module.css';
import { widgetValidator } from './widgetValidator';

const widgetRenderers = {
  HOME_PAGE_PRODUCT_LIST: (widget) => {
    const WidgetComponent = widgetRegistry.HOME_PAGE_PRODUCT_LIST;
    return <WidgetComponent key={widget.id} />;
  },
  STATIC_SEARCH_BAR: (widget) => {
    const WidgetComponent = widgetRegistry.STATIC_SEARCH_BAR;
    return <WidgetComponent key={widget.id} {...widget.data} />;
  },
  DIVIDER_WIDGET: (widget) => {
    const WidgetComponent = widgetRegistry.DIVIDER_WIDGET;
    if (widgetValidator.DIVIDER_WIDGET(widget.data))
      return <WidgetComponent key={widget.id} {...widget.data} />;
    console.error('Invalid data for widget type: DIVIDER_WIDGET');
    return null;
  },
  CURATION_WIDGET: (widget) => {
    const WidgetComponent = widgetRegistry.CURATION_WIDGET;
    if (widgetValidator.CURATION_WIDGET(widget.data))
      return <WidgetComponent key={widget.id} {...widget.data} />;
    console.error('Invalid data for widget type: CURATION_WIDGET');
    return null;
  },
  GENERIC_CTA_WIDGET: (widget) => {
    const WidgetComponent = widgetRegistry.GENERIC_CTA_WIDGET;
    if (widgetValidator.GENERIC_CTA_WIDGET(widget.data))
      return <WidgetComponent key={widget.id} {...widget.data} />;
    console.error('Invalid data for widget type: GENERIC_CTA_WIDGET');
    return null;
  },
  CATEGORY_WIDGET: (widget) => {
    const WidgetComponent = widgetRegistry.CATEGORY_WIDGET;
    if (widgetValidator.CATEGORY_WIDGET(widget.data))
      return <WidgetComponent key={widget.id} {...widget.data} />;
    return null;
  },
} satisfies { [K in WidgetType]: (widget: Widget<K>) => JSX.Element | null };

const WidgetRenderer = ({ widgets }: { widgets: Widget[] }): JSX.Element => {
  const groupedWidgets = useMemo(() => {
    const groups: Record<WidgetViewType, Widget[]> = {
      TOP_STICKY: [],
      BOTTOM_STICKY: [],
      NORMAL: [],
    };

    widgets.forEach((widget) => {
      groups[widget.viewType ?? WidgetViewType.NORMAL].push(widget);
    });

    return groups;
  }, [widgets]);

  const fallbackWidgetElement = (_widgetType: WidgetType): JSX.Element => {
    return (
      // TO-DO: <WidgetSkeleton type={widget.type} , rename _widgetType to widgetType then />
      <></>
    );
  };

  const renderWidget = (widget: Widget): JSX.Element | null => {
    try {
      const render = widgetRenderers[widget.type] as (
        w: Widget
      ) => JSX.Element | null;

      if (render === null)
        throw new Error(`Invalid data for widget type: ${widget.type}`);

      return (
        <Suspense fallback={fallbackWidgetElement(widget.type)}>
          {render(widget)}
        </Suspense>
      );
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  return (
    <div className={styles.widgetContainer}>
      {/* Top Sticky Area */}
      <div className={styles.topStickyContainer}>
        {groupedWidgets.TOP_STICKY.map((widget) => (
          <div key={widget.id}>{renderWidget(widget)}</div>
        ))}
      </div>

      {/* Main Content */}
      <div className={styles.scrollableContainer}>
        {groupedWidgets.NORMAL.map((widget) => (
          <div key={widget.id}>{renderWidget(widget)}</div>
        ))}
      </div>

      {/* Bottom Sticky Area */}
      <div className={styles.bottomStickyContainer}>
        {groupedWidgets.BOTTOM_STICKY.map((widget) => (
          <div key={widget.id}>{renderWidget(widget)}</div>
        ))}
      </div>
    </div>
  );
};

export default WidgetRenderer;
